/**
 * Builds copyable `mechanic execute aws timber download-logs` commands (Timber-Role)
 * for the Request Logs tab. Group paths follow the TimberFS SOP:
 * https://w.amazon.com/bin/view/Search/A9/CloudSearch/Ops/Template/TimberFS
 */

import { CONTAINERIZED_GROUPS, DOMAIN_SCOPED_SHARED_FILES } from './timberLogGroups';

export type MechanicStack = 'prod' | 'china' | 'us-gov';
export type LogGroupVariant = 'shared' | 'shared, domain-scoped' | 'containerized';

export interface LogGroupCandidate {
  group: string;
  /** Only the shared group needs `--search`; the others are already scoped to the domain. */
  needsSearch: boolean;
  variant: LogGroupVariant;
}

export interface TimberCommandEntry extends LogGroupCandidate {
  logGroupValue: string;
  command: string;
}

function hasDomainScopedSharedGroup(component: string, file: string): boolean {
  const family = /^(OSD?)_\d/.exec(component)?.[1] ?? component;
  return DOMAIN_SCOPED_SHARED_FILES[family]?.includes(file) ?? false;
}

const STAGE_WORDS: Record<string, string> = {
  prod: 'prod',
  beta: 'staging',
  gamma: 'integ',
  alpha: 'dev',
};

/** Timber-Role is not deployed in ISO partitions. */
export function isTimberRedirectSupported(partition: string): boolean {
  return !partition.startsWith('aws-iso');
}

function mechanicStackForPartition(partition: string): MechanicStack {
  if (partition === 'aws-cn') return 'china';
  if (partition === 'aws-us-gov') return 'us-gov';
  return 'prod';
}

/** Tumbler stage -> Timber stack suffix (`swift-<region>-<suffix>`). */
function stageWord(stage: string): string {
  return STAGE_WORDS[stage] ?? 'prod';
}

/**
 * Every group a log value may be archived in, since that depends on the domain's version and migration: the
 * shared group, plus the domain-scoped shared and containerized groups where they exist.
 */
export function buildFullLogGroups({
  region,
  stage,
  amiRelativeValue,
  clientId,
  domainName,
}: {
  region: string;
  stage: string;
  amiRelativeValue: string;
  clientId: string;
  domainName: string;
}): LogGroupCandidate[] {
  const stack = `swift-${region}-${stageWord(stage)}`;
  const domainPath = `${clientId}/${domainName}/`;
  const parts = amiRelativeValue.split('/');
  const [component, file] = [parts[0], parts[parts.length - 1]];

  const shared = `A9Search/${stack}/${amiRelativeValue}`;
  const candidates: LogGroupCandidate[] = [{ group: shared, needsSearch: true, variant: 'shared' }];
  if (hasDomainScopedSharedGroup(component, file)) {
    candidates.push({ group: `${shared}/${domainPath}`, needsSearch: false, variant: 'shared, domain-scoped' });
  }
  const containerized = CONTAINERIZED_GROUPS[file];
  if (containerized) {
    candidates.push({ group: `${containerized.replace('{stack}', stack)}/${domainPath}`, needsSearch: false, variant: 'containerized' });
  }
  return candidates;
}

/** Single-line command; `--stack` is omitted for the default `prod` stack. */
export function buildTimberDownloadCliCommand({
  account,
  airportCode,
  stack,
  group,
  startIso,
  endIso,
  clientId,
  domainName,
  needsSearch,
}: {
  account: string;
  airportCode: string;
  stack: MechanicStack;
  group: string;
  startIso: string;
  endIso: string;
  clientId: string;
  domainName: string;
  needsSearch: boolean;
}): string {
  return [
    'mechanic execute aws timber download-logs',
    `--account ${account}`,
    '--role-name Timber-Role',
    `--region ${airportCode}`,
    stack !== 'prod' && `--stack ${stack}`,
    `--group "${group}"`,
    needsSearch && `--search "client-id=${clientId},domain-name=${domainName}"`,
    `--start-time "${startIso}"`,
    `--end-time "${endIso}"`,
  ].filter(Boolean).join(' ');
}

/** One entry per candidate group of each selected log value. */
export function buildTimberDownloadCommands({
  account,
  region,
  airportCode,
  partition,
  stage,
  selectedLogGroupValues,
  clientId,
  domainName,
  startIso,
  endIso,
}: {
  account: string;
  region: string;
  airportCode: string;
  partition: string;
  stage: string;
  selectedLogGroupValues: string[];
  clientId: string;
  domainName: string;
  startIso: string;
  endIso: string;
}): TimberCommandEntry[] {
  const stack = mechanicStackForPartition(partition);

  return selectedLogGroupValues.flatMap(logGroupValue =>
    buildFullLogGroups({ region, stage, amiRelativeValue: logGroupValue, clientId, domainName }).map(candidate => ({
      ...candidate,
      logGroupValue,
      command: buildTimberDownloadCliCommand({
        account,
        airportCode,
        stack,
        group: candidate.group,
        needsSearch: candidate.needsSearch,
        startIso,
        endIso,
        clientId,
        domainName,
      }),
    })),
  );
}
